import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import TikTokIcon from "@/assets/tiktok";
import { Button } from "@/components/ui/button";
import { useTikTokAccess } from "@/hooks/useTikTokAccess";
import { getTikTokShopOauthUrl, type TikTokShopApp } from "@/services/tiktokShop.service";
import { BetaBadge } from "../tiktok/pre-release";
import { DashboardSection } from "./dashboard-section";
import { SalesByChannelCard } from "./sales-by-channel-card";
import { TikTokCreatorsCard } from "./tiktok-creators-card";
import { TikTokHeatmapCard } from "./tiktok-heatmap-card";
import { TikTokShopCard } from "./tiktok-shop-card";
import { TikTokVideosCard } from "./tiktok-videos-card";

/**
 * The section before any TikTok Shop is connected: what the analytics show
 * and the one action that fills them. The connect starts TikTok's
 * authorization directly, the same flow as the Integrations page.
 */
function ConnectCard({ app, available }: { app: TikTokShopApp; available: boolean }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await getTikTokShopOauthUrl(app);
      window.location.assign(url);
    } catch {
      toast.error("Could not start the TikTok Shop connection");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 sm:flex-row sm:items-center">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-muted">
        <TikTokIcon className="size-6 text-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold text-foreground">Connect your TikTok Shop</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Approve Reveshare's read-only access in Seller Center and this section fills with your TikTok
          GMV next to Shopify, your bestselling creators and videos, and the hours your shop sells.
        </p>
        {!available && (
          <p className="mt-2 text-xs text-muted-foreground">Connecting is not available on this server yet.</p>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" onClick={() => navigate("/brand/integrations")}>
          Integrations
        </Button>
        <Button disabled={busy || !available} onClick={connect}>
          Connect TikTok Shop
        </Button>
      </div>
    </div>
  );
}

/**
 * The TikTok section of the dashboard: the channel comparison first, then
 * TikTok-only depth (creators, content, hour-of-day pattern). Everything
 * TikTok wears the fixed teal channel color; Shopify stays brand purple.
 * It exists for brands with access to the module (the plan feature, or the
 * PRE_RELEASE_TIKTOK admin grant): a connect card until a shop is
 * connected, the analytics after. Everyone else sees the dashboard unchanged.
 */
export function TikTokRow({
  shopifyByDay,
  days,
}: {
  /** Daily Shopify affiliate revenue the dashboard already loads. */
  shopifyByDay: Record<string, number> | undefined;
  /** The dashboard's selected stats period (7/30/90). */
  days: number;
}) {
  const access = useTikTokAccess();
  if (!access.enabled) return null;

  // PRE_RELEASE_TIKTOK: the badge and the data-app-first connect go at GA.
  const badge = access.features.analytics === "beta" ? <BetaBadge size="sm" className="shadow-sm" /> : undefined;
  const connectApp: TikTokShopApp = access.preRelease ? "data" : "affiliate";
  // Same rule as the Integrations card: the app must be configured on the server.
  const connectAvailable = !!access.status?.[connectApp]?.enabled;

  return (
    <DashboardSection
      id="tiktok-analytics"
      icon={<TikTokIcon className="size-5 text-foreground" />}
      title="TikTok Shop"
      description="What's happening on TikTok? GMV next to Shopify, bestselling creators and content, and when the shop sells."
      badge={badge}
    >
      {access.connected ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <SalesByChannelCard shopifyByDay={shopifyByDay} days={days} />
            <TikTokShopCard />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TikTokCreatorsCard />
            <TikTokVideosCard />
          </div>
          <TikTokHeatmapCard />
        </>
      ) : (
        <ConnectCard app={connectApp} available={connectAvailable} />
      )}
    </DashboardSection>
  );
}
